import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = { open: "#FBBF24", in_progress: "#00E5FF", resolved: "#34D399", closed: "#52525B" };
const PRIO_COLORS = { low: "#52525B", normal: "#00E5FF", high: "#FBBF24", urgent: "#F472B6" };

export default function AdminTickets() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/tickets", { params }).then((r) => setItems(r.data.items)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    const { data } = await api.post(`/admin/tickets/${active.ticket_id}/reply`, { message: reply });
    setActive(data); setReply("");
    load();
    toast.success("Reply sent");
  };

  const updateStatus = async (status) => {
    const { data } = await api.patch(`/admin/tickets/${active.ticket_id}`, { status });
    setActive(data);
    load();
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline text-[#8A52FF]">[ SUPPORT ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight" data-testid="admin-tickets-title">Support tickets</h1>
          <p className="text-white/55 text-sm mt-1.5">Customer issues, requests, bug reports.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="bg-[#0A0A0E] border-white/10 w-44" data-testid="admin-tickets-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0E] border-white/10">
            <SelectItem value="all" className="focus:bg-white/5">All tickets</SelectItem>
            <SelectItem value="open" className="focus:bg-white/5">Open</SelectItem>
            <SelectItem value="in_progress" className="focus:bg-white/5">In progress</SelectItem>
            <SelectItem value="resolved" className="focus:bg-white/5">Resolved</SelectItem>
            <SelectItem value="closed" className="focus:bg-white/5">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-8 kr-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/40 border-b border-white/5">
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Subject</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">User</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Status</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Priority</th>
              <th className="text-right p-3 font-mono text-[10px] tracking-wider uppercase">Replies</th>
              <th className="text-left p-3 font-mono text-[10px] tracking-wider uppercase">Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.ticket_id} onClick={() => setActive(t)} className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`admin-ticket-row-${t.ticket_id}`}>
                <td className="p-3"><div className="text-white">{t.subject}</div><div className="text-xs text-white/40 capitalize">{t.category}</div></td>
                <td className="p-3">{t.user_email}</td>
                <td className="p-3"><span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border" style={{ borderColor: STATUS_COLORS[t.status] + "55", color: STATUS_COLORS[t.status] }}>{t.status}</span></td>
                <td className="p-3"><span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border" style={{ borderColor: PRIO_COLORS[t.priority] + "55", color: PRIO_COLORS[t.priority] }}>{t.priority}</span></td>
                <td className="p-3 text-right font-mono">{t.replies?.length || 0}</td>
                <td className="p-3 text-xs text-white/50">{new Date(t.updated_at).toLocaleString()}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-12 text-center text-white/40"><MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-40" /> No tickets in this filter.</td></tr>}
          </tbody>
        </table>
      </div>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent side="right" className="bg-[#0A0A0E] border-white/10 text-white w-[480px] sm:max-w-[480px] overflow-y-auto">
          {active && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display">{active.subject}</SheetTitle>
                <SheetDescription className="text-white/50">{active.user_email} · {active.category}</SheetDescription>
              </SheetHeader>

              <div className="mt-4 flex gap-2">
                {["open", "in_progress", "resolved", "closed"].map((s) => (
                  <Button key={s} size="sm" onClick={() => updateStatus(s)} variant={active.status === s ? "default" : "outline"} className={active.status === s ? "bg-[#8A52FF] hover:bg-[#8A52FF]/90 text-white text-xs" : "border-white/10 hover:bg-white/5 hover:text-white text-xs capitalize"} data-testid={`ticket-status-${s}`}>
                    {s.replace("_", " ")}
                  </Button>
                ))}
              </div>

              <div className="mt-6 border-l-2 border-[#00E5FF]/40 pl-4">
                <div className="kr-overline">USER MESSAGE</div>
                <div className="text-sm mt-2 whitespace-pre-wrap">{active.message}</div>
              </div>

              <div className="mt-6 space-y-3">
                {(active.replies || []).map((r) => (
                  <div key={r.at + r.author} className={`p-3 border ${r.author === "admin" ? "border-[#8A52FF]/30 bg-[#8A52FF]/5" : "border-white/10 bg-white/[0.02]"}`}>
                    <div className="flex items-center justify-between">
                      <div className="kr-overline" style={{ color: r.author === "admin" ? "#8A52FF" : "#00E5FF" }}>{r.author === "admin" ? "ADMIN" : "USER"} · {r.author_name}</div>
                      <div className="text-[10px] text-white/40 font-mono">{new Date(r.at).toLocaleString()}</div>
                    </div>
                    <div className="text-sm mt-2 whitespace-pre-wrap">{r.message}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6">
                <div className="kr-overline mb-2">REPLY AS ADMIN</div>
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} placeholder="Type your reply…" className="bg-[#050505] border-white/10 resize-none" data-testid="ticket-reply-input" />
                <Button onClick={sendReply} disabled={!reply.trim()} className="mt-3 bg-[#8A52FF] hover:bg-[#8A52FF]/90 text-white w-full" data-testid="ticket-reply-send"><Send className="h-4 w-4 mr-2" /> Send reply</Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
